import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PermCtx = createContext(null);

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState({});     // {module: [actions]}
  const [visibleModules, setVisibleModules] = useState([]);
  const [role, setRole] = useState(null);
  const [businessUnitId, setBusinessUnitId] = useState(null);
  const [clientName, setClientName] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!user) { setLoaded(false); return; }
    try {
      const { data } = await api.get("/org/me/permissions");
      setRole(data.role);
      setBusinessUnitId(data.business_unit_id);
      setClientName(data.client_id || data.client_name);
      setPermissions(data.permissions || {});
      setVisibleModules(data.visible_modules || []);
      setLoaded(true);
    } catch {
      // Pre-RBAC user or auth issue — default to safest
      setRole("field_worker");
      setPermissions({});
      setVisibleModules([]);
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const can = useCallback((module, action) => {
    const acts = permissions[module] || [];
    return acts.includes(action);
  }, [permissions]);

  const canSee = useCallback((module) => visibleModules.includes(module), [visibleModules]);

  return (
    <PermCtx.Provider value={{
      role, businessUnitId, clientName, permissions, visibleModules,
      can, canSee, loaded, refresh: load,
    }}>
      {children}
    </PermCtx.Provider>
  );
}

export const usePermissions = () => useContext(PermCtx);
